import React, { useEffect, useState } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import loadingLogo from "../../../assets/emlogo_loading_transparent.gif";
import { useFxExecutionDecision } from "../Utils/CoTraderUtils";
import { formatTimeWithZone } from "../Utils/timezone";
import { useDisplayTimeZone } from "../Utils/displayTimeZone";

const ALGO_COLOR = "#2FB171";
const RFQ_COLOR = "#D9534F";
const DRAFT_AMOUNT_VALUE_KEY = "cotrader_fx_execution_amount_value_draft";
const DRAFT_AMOUNT_UNIT_KEY = "cotrader_fx_execution_amount_unit_draft";
const DRAFT_SIDE_KEY = "cotrader_fx_execution_side_draft";
const APPLIED_AMOUNT_VALUE_KEY = "cotrader_fx_execution_amount_value_applied";
const APPLIED_AMOUNT_UNIT_KEY = "cotrader_fx_execution_amount_unit_applied";
const APPLIED_SIDE_KEY = "cotrader_fx_execution_side_applied";
const LEGACY_DRAFT_AMOUNT_KEY = "cotrader_fx_execution_amount_draft";
const LEGACY_APPLIED_AMOUNT_KEY = "cotrader_fx_execution_amount_applied";

const AMOUNT_UNIT_OPTIONS = [
  { value: "thousand", label: "Thousand" },
  { value: "million", label: "Million" },
  { value: "billion", label: "Billion" },
];

const SIDE_OPTIONS = [
  { value: "buy_base", label: "Buy Base" },
  { value: "sell_base", label: "Sell Base" },
  { value: "buy_quote", label: "Buy Quote" },
  { value: "sell_quote", label: "Sell Quote" },
];

const normalizeStoredUnit = (rawUnit: unknown) => {
  const unit = String(rawUnit || "")
    .trim()
    .toLowerCase();

  if (unit === "b" || unit === "bn" || unit === "billion") return "billion";
  if (
    unit === "m" ||
    unit === "mm" ||
    unit === "mn" ||
    unit === "million"
  ) {
    return "million";
  }
  if (unit === "k" || unit === "thousand") return "thousand";

  return "million";
};

const inferAmountPartsFromNumeric = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) {
    return { amountValue: "50", amountUnit: "million" };
  }

  if (value >= 1_000_000_000) {
    return {
      amountValue: String(Number((value / 1_000_000_000).toFixed(2))),
      amountUnit: "billion",
    };
  }

  if (value >= 1_000_000) {
    return {
      amountValue: String(Number((value / 1_000_000).toFixed(2))),
      amountUnit: "million",
    };
  }

  if (value >= 1_000) {
    return {
      amountValue: String(Number((value / 1_000).toFixed(2))),
      amountUnit: "thousand",
    };
  }

  return {
    amountValue: String(Number(value.toFixed(2))),
    amountUnit: "thousand",
  };
};

const parseLegacyAmountParts = (rawAmount: unknown) => {
  if (!rawAmount) {
    return { amountValue: "50", amountUnit: "million" };
  }

  const rawText = String(rawAmount).trim();
  const cleanedText = rawText.replace(/,/g, "").replace(/\$/g, "");
  const match = cleanedText.match(
    /(\d+(?:\.\d+)?)\s*(billion|bn|b|million|mm|mn|m|thousand|k)?/i
  );

  const parsedValue = Number(match?.[1]);
  const parsedUnit = normalizeStoredUnit(match?.[2]);

  if (Number.isFinite(parsedValue) && match?.[2]) {
    return {
      amountValue: String(parsedValue),
      amountUnit: parsedUnit,
    };
  }

  if (Number.isFinite(parsedValue)) {
    return inferAmountPartsFromNumeric(parsedValue);
  }

  return { amountValue: "50", amountUnit: "million" };
};

const composeTradeAmount = (amountValue: string, amountUnit: string) =>
  `${String(amountValue || "").trim()} ${normalizeStoredUnit(amountUnit)}`.trim();

const getConfidenceTone = (confidence: number | null) => {
  if (confidence == null) return "Preparing";
  if (confidence >= 80) return "High confidence";
  if (confidence >= 60) return "Moderate confidence";
  return "Measured confidence";
};

interface CoTraderExecutionDecisionComponentProps {
  currencyPair: string;
  language?: string;
}

const CoTraderExecutionDecisionComponent: React.FunctionComponent<
  CoTraderExecutionDecisionComponentProps
> = ({ currencyPair, language = "English" }) => {
  const theme = useTheme();
  const { timeZone } = useDisplayTimeZone();
  const [tradeAmountValue, setTradeAmountValue] = useState(() => {
    const savedDraftValue = localStorage.getItem(DRAFT_AMOUNT_VALUE_KEY);
    const savedAppliedValue = localStorage.getItem(APPLIED_AMOUNT_VALUE_KEY);
    const legacyDraftAmount = localStorage.getItem(LEGACY_DRAFT_AMOUNT_KEY);
    const legacyAppliedAmount = localStorage.getItem(LEGACY_APPLIED_AMOUNT_KEY);

    if (savedDraftValue) return savedDraftValue;
    if (savedAppliedValue) return savedAppliedValue;

    return parseLegacyAmountParts(legacyDraftAmount || legacyAppliedAmount)
      .amountValue;
  });
  const [tradeAmountUnit, setTradeAmountUnit] = useState(() => {
    const savedDraftUnit = localStorage.getItem(DRAFT_AMOUNT_UNIT_KEY);
    const savedAppliedUnit = localStorage.getItem(APPLIED_AMOUNT_UNIT_KEY);
    const legacyDraftAmount = localStorage.getItem(LEGACY_DRAFT_AMOUNT_KEY);
    const legacyAppliedAmount = localStorage.getItem(LEGACY_APPLIED_AMOUNT_KEY);

    if (savedDraftUnit) return normalizeStoredUnit(savedDraftUnit);
    if (savedAppliedUnit) return normalizeStoredUnit(savedAppliedUnit);

    return parseLegacyAmountParts(legacyDraftAmount || legacyAppliedAmount)
      .amountUnit;
  });
  const [selectedSide, setSelectedSide] = useState(() => {
    const savedDraft = localStorage.getItem(DRAFT_SIDE_KEY);
    const savedApplied = localStorage.getItem(APPLIED_SIDE_KEY);
    return savedDraft || savedApplied || "buy_base";
  });
  const [submittedTradeAmountValue, setSubmittedTradeAmountValue] = useState(
    () => {
      const savedAppliedValue = localStorage.getItem(APPLIED_AMOUNT_VALUE_KEY);
      const legacyAppliedAmount = localStorage.getItem(LEGACY_APPLIED_AMOUNT_KEY);

      if (savedAppliedValue) return savedAppliedValue;

      return parseLegacyAmountParts(legacyAppliedAmount).amountValue;
    }
  );
  const [submittedTradeAmountUnit, setSubmittedTradeAmountUnit] = useState(
    () => {
      const savedAppliedUnit = localStorage.getItem(APPLIED_AMOUNT_UNIT_KEY);
      const legacyAppliedAmount = localStorage.getItem(LEGACY_APPLIED_AMOUNT_KEY);

      if (savedAppliedUnit) return normalizeStoredUnit(savedAppliedUnit);

      return parseLegacyAmountParts(legacyAppliedAmount).amountUnit;
    }
  );
  const [submittedTradeAmount, setSubmittedTradeAmount] = useState(() => {
    const savedAppliedValue = localStorage.getItem(APPLIED_AMOUNT_VALUE_KEY);
    const savedAppliedUnit = localStorage.getItem(APPLIED_AMOUNT_UNIT_KEY);
    const legacyAppliedAmount = localStorage.getItem(LEGACY_APPLIED_AMOUNT_KEY);

    if (savedAppliedValue && savedAppliedUnit) {
      return composeTradeAmount(savedAppliedValue, savedAppliedUnit);
    }

    const legacyParts = parseLegacyAmountParts(legacyAppliedAmount);
    return composeTradeAmount(legacyParts.amountValue, legacyParts.amountUnit);
  });
  const [submittedSide, setSubmittedSide] = useState(() => {
    return localStorage.getItem(APPLIED_SIDE_KEY) || "buy_base";
  });
  const [requestSeed, setRequestSeed] = useState(0);
  const [inputError, setInputError] = useState("");

  const colors = {
    background: theme.globallink.colors.Grey[1100],
    surface: theme.globallink.colors.Grey[1200],
    border: "rgba(255,255,255,0.08)",
    textPrimary: theme.globallink.colors.White[500],
    textSecondary: theme.globallink.colors.Grey[500],
    textMuted: theme.globallink.colors.Grey[600],
    badgeBg: "rgba(67, 125, 255, 0.16)",
    badgeBorder: "rgba(67, 125, 255, 0.28)",
    inputBg: "rgba(255,255,255,0.04)",
    accent: theme.globallink.colors.Blue[500],
  };

  const useStyles = makeStyles({
    container: {
      height: "100%",
      width: "100%",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      padding: "14px",
      backgroundColor: colors.background,
      overflowY: "auto",
    },
    card: {
      borderRadius: "12px",
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      padding: "14px",
    },
    sectionLabel: {
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: colors.textMuted,
      marginBottom: "10px",
    },
    controlRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: "10px",
      alignItems: "flex-end",
    },
    controlGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      minWidth: 0,
      flex: "1 1 140px",
    },
    controlGroupCompact: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      minWidth: 130,
      flex: "0 1 160px",
    },
    inputLabel: {
      fontSize: "12px",
      fontWeight: 700,
      color: colors.textSecondary,
    },
    input: {
      width: "100%",
      height: "38px",
      borderRadius: "10px",
      border: `1px solid ${colors.border}`,
      background: colors.inputBg,
      color: colors.textPrimary,
      padding: "0 12px",
      outline: "none",
    },
    actionButton: {
      height: "38px",
      minWidth: "148px",
      borderRadius: "10px",
      border: "1px solid transparent",
      background: colors.accent,
      color: "#FFFFFF",
      fontWeight: 700,
      cursor: "pointer",
      padding: "0 14px",
      "&:disabled": {
        opacity: 0.72,
        cursor: "not-allowed",
      },
    },
    helperRow: {
      display: "flex",
      justifyContent: "space-between",
      gap: "8px",
      flexWrap: "wrap",
      marginTop: "10px",
      fontSize: "12px",
      color: colors.textSecondary,
    },
    inlineCode: {
      fontFamily: "monospace",
      color: colors.textPrimary,
    },
    errorText: {
      marginTop: "10px",
      fontSize: "12px",
      color: RFQ_COLOR,
    },
    stateCard: {
      flex: 1,
      minHeight: "220px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: "12px",
      textAlign: "center",
      color: colors.textSecondary,
      borderRadius: "12px",
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      padding: "24px",
    },
    stateTitle: {
      color: colors.textPrimary,
      fontWeight: 700,
    },
    resultHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "12px",
      flexWrap: "wrap",
    },
    resultHeading: {
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: colors.textMuted,
    },
    resultPrimary: {
      fontSize: "30px",
      lineHeight: "34px",
      fontWeight: 800,
      color: colors.textPrimary,
      marginTop: "4px",
    },
    resultSubtle: {
      fontSize: "12px",
      color: colors.textSecondary,
      marginTop: "4px",
    },
    confidencePill: {
      minWidth: "120px",
      textAlign: "center",
      borderRadius: "22px",
      padding: "8px 12px",
      background: colors.badgeBg,
      border: `1px solid ${colors.badgeBorder}`,
      color: colors.textPrimary,
      fontWeight: 700,
      fontSize: "12px",
    },
    confidenceTone: {
      fontSize: "11px",
      color: colors.textSecondary,
      marginTop: "2px",
    },
    sliderWrap: {
      marginTop: "14px",
    },
    sliderTrack: {
      position: "relative",
      height: "14px",
      borderRadius: "999px",
      overflow: "hidden",
      background:
        "linear-gradient(90deg, rgba(217,83,79,0.28) 0%, rgba(67,207,230,0.16) 50%, rgba(47,177,113,0.28) 100%)",
      border: `1px solid ${colors.border}`,
    },
    sliderCenterLine: {
      position: "absolute",
      top: 0,
      bottom: 0,
      left: "50%",
      width: "1px",
      background: "rgba(255,255,255,0.26)",
      transform: "translateX(-50%)",
    },
    sliderLabels: {
      marginTop: "8px",
      display: "flex",
      justifyContent: "space-between",
      gap: "8px",
      fontSize: "12px",
      color: colors.textSecondary,
    },
    summaryCard: {
      marginTop: "14px",
      borderRadius: "12px",
      padding: "14px",
    },
    summaryTitle: {
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      marginBottom: "6px",
    },
    summaryBody: {
      color: colors.textPrimary,
      lineHeight: 1.5,
      fontSize: "13px",
    },
    supportingList: {
      marginTop: "12px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    supportingCard: {
      borderRadius: "12px",
      background: "rgba(255,255,255,0.03)",
      border: `1px solid ${colors.border}`,
      padding: "12px",
    },
    supportingTitle: {
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: colors.textSecondary,
      marginBottom: "4px",
    },
    supportingBody: {
      color: colors.textPrimary,
      lineHeight: 1.45,
      fontSize: "13px",
    },
    statusText: {
      marginTop: "12px",
      fontSize: "12px",
      color: colors.textSecondary,
    },
    loadingEllipsis: {
      marginLeft: "2px",
      "& span": {
        animation: "$blink 1.6s infinite both",
      },
      "& span:nth-child(2)": { animationDelay: "0.2s" },
      "& span:nth-child(3)": { animationDelay: "0.4s" },
    },
    "@keyframes blink": {
      "0%, 80%, 100%": { opacity: 0 },
      "40%": { opacity: 1 },
    },
  });

  const classes = useStyles();

  // Server progress messages end in "..." while work is ongoing; swap those
  // static dots for pulsing ones so the text reads as in-progress.
  const renderStatusText = (text: string) => {
    const match = text.match(/^([\s\S]*?)\s*(?:\.{2,}|\u2026)$/);
    if (!match) return text;

    return (
      <>
        {match[1]}
        <span className={classes.loadingEllipsis} aria-hidden="true">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
      </>
    );
  };

  useEffect(() => {
    localStorage.setItem(DRAFT_AMOUNT_VALUE_KEY, tradeAmountValue);
  }, [tradeAmountValue]);

  useEffect(() => {
    localStorage.setItem(DRAFT_AMOUNT_UNIT_KEY, tradeAmountUnit);
  }, [tradeAmountUnit]);

  useEffect(() => {
    localStorage.setItem(DRAFT_SIDE_KEY, selectedSide);
  }, [selectedSide]);

  const { decision, explanation, loading, error, statusText, updatedAt } =
    useFxExecutionDecision({
      currencyPair,
      tradeAmount: submittedTradeAmount,
      side: submittedSide,
      language,
      requestSeed,
      enabled: Boolean(currencyPair),
    });

  const normalizedTradeAmountValue = String(tradeAmountValue || "").trim();
  const normalizedTradeAmountUnit = normalizeStoredUnit(tradeAmountUnit);
  const normalizedSelectedSide = selectedSide.trim();
  const hasPendingChanges =
    normalizedTradeAmountValue !== submittedTradeAmountValue ||
    normalizedTradeAmountUnit !== submittedTradeAmountUnit ||
    normalizedSelectedSide !== submittedSide;

  const handleRunRecommendation = () => {
    if (!normalizedTradeAmountValue) {
      setInputError("Enter a trade amount to generate the recommendation.");
      return;
    }

    const numericTradeAmount = Number(normalizedTradeAmountValue);
    if (!Number.isFinite(numericTradeAmount) || numericTradeAmount <= 0) {
      setInputError("Enter a positive number for the trade amount.");
      return;
    }

    const composedTradeAmount = composeTradeAmount(
      normalizedTradeAmountValue,
      normalizedTradeAmountUnit
    );

    setInputError("");
    setSubmittedTradeAmountValue(normalizedTradeAmountValue);
    setSubmittedTradeAmountUnit(normalizedTradeAmountUnit);
    setSubmittedTradeAmount(composedTradeAmount);
    setSubmittedSide(normalizedSelectedSide || "buy_base");
    localStorage.setItem(APPLIED_AMOUNT_VALUE_KEY, normalizedTradeAmountValue);
    localStorage.setItem(APPLIED_AMOUNT_UNIT_KEY, normalizedTradeAmountUnit);
    localStorage.setItem(LEGACY_APPLIED_AMOUNT_KEY, composedTradeAmount);
    localStorage.setItem(APPLIED_SIDE_KEY, normalizedSelectedSide || "buy_base");
    setRequestSeed((current) => current + 1);
  };

  const handleAmountKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    handleRunRecommendation();
  };

  const algoPercent =
    decision?.algo == null ? null : Math.round(decision.algo * 100);
  const rfqPercent = decision?.rfq == null ? null : Math.round(decision.rfq * 100);
  const sliderPosition =
    decision?.algo == null
      ? 50
      : Math.max(0, Math.min(100, decision.algo * 100));
  const recommendationColor =
    decision?.recommended === "rfq" ? RFQ_COLOR : ALGO_COLOR;
  const recommendationLabel =
    decision?.recommended === "rfq" ? "RFQ" : "Algo";

  const explanationEntries = Object.entries(explanation || {}).filter(
    ([title, detail]) => title && detail
  );
  const recommendationSummary =
    explanationEntries.find(
      ([title]) => title.toLowerCase() === "recommendation"
    ) ||
    explanationEntries[0] ||
    null;
  const supportingEntries = explanationEntries.filter(
    ([title, detail]) =>
      !recommendationSummary ||
      title !== recommendationSummary[0] ||
      detail !== recommendationSummary[1]
  );

  return (
    <div className={classes.container}>
      <div className={classes.card}>
        <div className={classes.sectionLabel}>Trade Setup</div>

        <div className={classes.controlRow}>
          <div className={classes.controlGroup}>
            <label
              className={classes.inputLabel}
              htmlFor="bestx-execution-amount"
            >
              Amount
            </label>
            <input
              id="bestx-execution-amount"
              className={classes.input}
              type="number"
              min="0"
              step="any"
              value={tradeAmountValue}
              onChange={(event) => {
                setTradeAmountValue(event.target.value);
                if (inputError) setInputError("");
              }}
              onKeyDown={handleAmountKeyDown}
              placeholder="50"
            />
          </div>

          <div className={classes.controlGroupCompact}>
            <label
              className={classes.inputLabel}
              htmlFor="bestx-execution-amount-unit"
            >
              Size
            </label>
            <select
              id="bestx-execution-amount-unit"
              className={classes.input}
              value={tradeAmountUnit}
              onChange={(event) => setTradeAmountUnit(event.target.value)}
            >
              {AMOUNT_UNIT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className={classes.controlGroupCompact}>
            <label
              className={classes.inputLabel}
              htmlFor="bestx-execution-side"
            >
              Side
            </label>
            <select
              id="bestx-execution-side"
              className={classes.input}
              value={selectedSide}
              onChange={(event) => setSelectedSide(event.target.value)}
            >
              {SIDE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className={classes.actionButton}
            onClick={handleRunRecommendation}
            disabled={loading}
            style={{
              background: loading ? `${colors.accent}B8` : colors.accent,
            }}
          >
            {loading ? "Updating..." : "Update Decision"}
          </button>
        </div>

        <div className={classes.helperRow}>
          <span>
            Applied amount:{" "}
            <span className={classes.inlineCode}>
              {composeTradeAmount(
                submittedTradeAmountValue,
                submittedTradeAmountUnit
              )}
            </span>
          </span>
          <span>{hasPendingChanges ? "Unsaved trade inputs" : "Inputs applied"}</span>
        </div>

        {inputError ? <div className={classes.errorText}>{inputError}</div> : null}
      </div>

      {loading && !decision && !error ? (
        <div className={classes.stateCard}>
          <img
            src={loadingLogo}
            alt=""
            width={40}
            height={40}
            decoding="async"
          />
          <div className={classes.stateTitle}>
            Preparing Algo vs RFQ recommendation
          </div>
          <div>{statusText ? renderStatusText(statusText) : `Collecting execution context for ${currencyPair}.`}</div>
        </div>
      ) : null}

      {error && !decision ? (
        <div className={classes.stateCard}>
          <div className={classes.stateTitle}>
            Algo vs RFQ recommendation is unavailable.
          </div>
          <div>{error}</div>
        </div>
      ) : null}

      {!loading && !error && !decision ? (
        <div className={classes.stateCard}>
          <div className={classes.stateTitle}>No Algo vs RFQ recommendation yet.</div>
          <div>{statusText || "Update the trade inputs to generate a recommendation."}</div>
        </div>
      ) : null}

      {decision ? (
        <div className={classes.card}>
          <div className={classes.resultHeader}>
            <div>
              <div className={classes.resultHeading}>Algo vs RFQ</div>
              <div className={classes.resultPrimary}>{recommendationLabel}</div>
              <div className={classes.resultSubtle}>
                {updatedAt == null
                  ? `Waiting for ${currencyPair} decision`
                  : `Last updated ${formatTimeWithZone(updatedAt, timeZone)}`}
              </div>
            </div>

            <div className={classes.confidencePill}>
              <div>{decision.confidence ?? "--"}% confidence</div>
              <div className={classes.confidenceTone}>
                {getConfidenceTone(decision.confidence)}
              </div>
            </div>
          </div>

          <div className={classes.sliderWrap}>
            <div className={classes.sliderTrack}>
              <div className={classes.sliderCenterLine} />
              <div
                style={{
                  position: "absolute",
                  top: -4,
                  left: `${sliderPosition}%`,
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  transform: "translateX(-50%)",
                  background: recommendationColor,
                  border: `2px solid ${colors.background}`,
                  boxShadow: "0 0 0 2px rgba(255,255,255,0.08)",
                }}
              />
            </div>

            <div className={classes.sliderLabels}>
              <span>RFQ {rfqPercent == null ? "--" : `${rfqPercent}%`}</span>
              <span>
                {decision.confidence == null
                  ? ""
                  : `${decision.confidence}% confidence`}
              </span>
              <span>Algo {algoPercent == null ? "--" : `${algoPercent}%`}</span>
            </div>
          </div>

          {recommendationSummary ? (
            <div
              className={classes.summaryCard}
              style={{
                background: `${recommendationColor}12`,
                border: `1px solid ${recommendationColor}2A`,
              }}
            >
              <div
                className={classes.summaryTitle}
                style={{ color: recommendationColor }}
              >
                {recommendationSummary[0]}
              </div>
              <div className={classes.summaryBody}>{recommendationSummary[1]}</div>
            </div>
          ) : null}

          {supportingEntries.length > 0 ? (
            <div className={classes.supportingList}>
              {supportingEntries.map(([title, detail]) => (
                <div key={title} className={classes.supportingCard}>
                  <div className={classes.supportingTitle}>{title}</div>
                  <div className={classes.supportingBody}>{detail}</div>
                </div>
              ))}
            </div>
          ) : null}

          {statusText ? <div className={classes.statusText}>{renderStatusText(statusText)}</div> : null}
        </div>
      ) : null}
    </div>
  );
};

export default CoTraderExecutionDecisionComponent;
